import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from './config';
import { Service, Appointment, BlockedTime, User, TeamMember, Testimonial, Category, SalonSettings, DEFAULT_SALON_SETTINGS, Product, Order, SpecialRequest } from '../types';

// Salon Settings API (single document: settings/salon)
export const salonSettingsApi = {
  get: async (): Promise<SalonSettings> => {
    const settingsRef = doc(db, 'settings', 'salon');
    const snap = await getDoc(settingsRef);
    if (snap.exists()) {
      return snap.data() as SalonSettings;
    }
    return DEFAULT_SALON_SETTINGS;
  },

  save: async (settings: SalonSettings): Promise<void> => {
    const settingsRef = doc(db, 'settings', 'salon');
    const snap = await getDoc(settingsRef);
    if (snap.exists()) {
      await updateDoc(settingsRef, settings as any);
    } else {
      const { setDoc } = await import('firebase/firestore');
      await setDoc(settingsRef, settings);
    }
  },
};

// Categories API
export const categoriesApi = {
  getAll: async (): Promise<Category[]> => {
    const ref = collection(db, 'categories');
    const q = query(ref, orderBy('name', 'asc'));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Category));
  },

  create: async (category: Omit<Category, 'id'>): Promise<string> => {
    const ref = collection(db, 'categories');
    const docRef = await addDoc(ref, category);
    return docRef.id;
  },

  update: async (id: string, category: Partial<Category>): Promise<void> => {
    const ref = doc(db, 'categories', id);
    await updateDoc(ref, category);
  },

  delete: async (id: string): Promise<void> => {
    const ref = doc(db, 'categories', id);
    await deleteDoc(ref);
  }
};

// Services API
export const servicesApi = {
  // Get all services
  getAll: async (): Promise<Service[]> => {
    const servicesRef = collection(db, 'services');
    const snapshot = await getDocs(servicesRef);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Service));
  },

  // Get service by ID
  getById: async (id: string): Promise<Service | null> => {
    const serviceRef = doc(db, 'services', id);
    const serviceDoc = await getDoc(serviceRef);
    
    if (serviceDoc.exists()) {
      return { id: serviceDoc.id, ...serviceDoc.data() } as Service;
    }
    
    return null;
  },

  // Create new service
  create: async (service: Omit<Service, 'id'>): Promise<string> => {
    const servicesRef = collection(db, 'services');
    const docRef = await addDoc(servicesRef, service);
    return docRef.id;
  },

  // Update service
  update: async (id: string, service: Partial<Service>): Promise<void> => {
    const serviceRef = doc(db, 'services', id);
    await updateDoc(serviceRef, service);
  },

  // Delete service
  delete: async (id: string): Promise<void> => {
    const serviceRef = doc(db, 'services', id);
    await deleteDoc(serviceRef);
  },

  // Upload service image
  uploadImage: async (file: File, serviceId: string): Promise<string> => {
    const storageRef = ref(storage, `services/${serviceId}/${file.name}`);
    await uploadBytes(storageRef, file);
    return getDownloadURL(storageRef);
  }
};

// Appointments API
export const appointmentsApi = {
  // Get all appointments
  getAll: async (): Promise<Appointment[]> => {
    const appointmentsRef = collection(db, 'appointments');
    const snapshot = await getDocs(appointmentsRef);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Appointment));
  },

  // Get appointment by ID
  getById: async (id: string): Promise<Appointment | null> => {
    const appointmentRef = doc(db, 'appointments', id);
    const appointmentDoc = await getDoc(appointmentRef);
    
    if (appointmentDoc.exists()) {
      return { id: appointmentDoc.id, ...appointmentDoc.data() } as Appointment;
    }
    
    return null;
  },

  // Get appointments by client ID
  getByClientId: async (clientId: string): Promise<Appointment[]> => {
    const appointmentsRef = collection(db, 'appointments');
    const q = query(
      appointmentsRef, 
      where('clientId', '==', clientId),
      orderBy('date', 'desc'),
      orderBy('startTime', 'desc')
    );
    
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Appointment));
  },

  // Get appointments for a specific date
  getByDate: async (date: string): Promise<Appointment[]> => {
    const appointmentsRef = collection(db, 'appointments');
    const q = query(
      appointmentsRef, 
      where('date', '==', date),
      orderBy('startTime', 'asc')
    );
    
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Appointment));
  },

  // Create new appointment
  create: async (appointment: Omit<Appointment, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> => {
    const appointmentsRef = collection(db, 'appointments');
    const now = new Date().toISOString();

    const raw = { ...appointment, createdAt: now, updatedAt: now };
    const newAppointment = Object.fromEntries(
      Object.entries(raw).filter(([, v]) => v !== undefined)
    );

    const docRef = await addDoc(appointmentsRef, newAppointment);
    return docRef.id;
  },

  // Update appointment
  update: async (id: string, appointment: Partial<Appointment>): Promise<void> => {
    const appointmentRef = doc(db, 'appointments', id);
    
    await updateDoc(appointmentRef, {
      ...appointment,
      updatedAt: new Date().toISOString()
    });
  },

  // Delete appointment
  delete: async (id: string): Promise<void> => {
    const appointmentRef = doc(db, 'appointments', id);
    await deleteDoc(appointmentRef);
  }
};

// Blocked Times API
export const blockedTimesApi = {
  // Get all blocked times
  getAll: async (): Promise<BlockedTime[]> => {
    const blockedTimesRef = collection(db, 'blockedTimes');
    const snapshot = await getDocs(blockedTimesRef);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as BlockedTime));
  },

  // Create new blocked time
  create: async (blockedTime: Omit<BlockedTime, 'id'>): Promise<string> => {
    const blockedTimesRef = collection(db, 'blockedTimes');
    const docRef = await addDoc(blockedTimesRef, blockedTime);
    return docRef.id;
  },

  // Update blocked time
  update: async (id: string, blockedTime: Partial<BlockedTime>): Promise<void> => {
    const blockedTimeRef = doc(db, 'blockedTimes', id);
    await updateDoc(blockedTimeRef, blockedTime);
  },

  // Delete blocked time
  delete: async (id: string): Promise<void> => {
    const blockedTimeRef = doc(db, 'blockedTimes', id);
    await deleteDoc(blockedTimeRef);
  }
};

// Team Members API
export const teamApi = {
  // Get all team members
  getAll: async (): Promise<TeamMember[]> => {
    const teamRef = collection(db, 'team');
    const q = query(teamRef, orderBy('name', 'asc'));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as TeamMember));
  },

  // Get team member by ID
  getById: async (id: string): Promise<TeamMember | null> => {
    const memberRef = doc(db, 'team', id);
    const memberDoc = await getDoc(memberRef);
    
    if (memberDoc.exists()) {
      return { id: memberDoc.id, ...memberDoc.data() } as TeamMember;
    }
    
    return null;
  },

  // Create new team member
  create: async (member: Omit<TeamMember, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> => {
    const teamRef = collection(db, 'team');
    const now = new Date().toISOString();
    
    const newMember = {
      ...member,
      createdAt: now,
      updatedAt: now
    };
    
    const docRef = await addDoc(teamRef, newMember);
    return docRef.id;
  },

  // Update team member
  update: async (id: string, member: Partial<TeamMember>): Promise<void> => {
    const memberRef = doc(db, 'team', id);
    
    await updateDoc(memberRef, {
      ...member,
      updatedAt: new Date().toISOString()
    });
  },

  // Delete team member
  delete: async (id: string): Promise<void> => {
    const memberRef = doc(db, 'team', id);
    await deleteDoc(memberRef);
  },

  // Upload team member image
  uploadImage: async (file: File, memberId: string): Promise<string> => {
    const storageRef = ref(storage, `team/${memberId}/${file.name}`);
    await uploadBytes(storageRef, file);
    return getDownloadURL(storageRef);
  }
};

// Users API
export const usersApi = {
  // Get user by ID
  getById: async (id: string): Promise<User | null> => {
    const userRef = doc(db, 'users', id);
    const userDoc = await getDoc(userRef);
    
    if (userDoc.exists()) {
      return { id: userDoc.id, ...userDoc.data() } as User;
    }
    
    return null;
  },

  // Update user
  update: async (id: string, userData: Partial<User>): Promise<void> => {
    const userRef = doc(db, 'users', id);
    await updateDoc(userRef, userData);
  }
};

// Testimonials API
export const testimonialsApi = {
  // Get all testimonials
  getAll: async (): Promise<Testimonial[]> => {
    const testimonialsRef = collection(db, 'testimonials');
    const q = query(testimonialsRef, orderBy('createdAt', 'desc'));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Testimonial));
  },

  // Get active testimonials only
  getActive: async (): Promise<Testimonial[]> => {
    const testimonialsRef = collection(db, 'testimonials');
    const q = query(
      testimonialsRef, 
      where('isActive', '==', true),
      orderBy('createdAt', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Testimonial));
  },

  // Get featured testimonials
  getFeatured: async (): Promise<Testimonial[]> => {
    const testimonialsRef = collection(db, 'testimonials');
    const q = query(
      testimonialsRef, 
      where('isActive', '==', true),
      where('featured', '==', true),
      orderBy('createdAt', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Testimonial));
  },

  // Get testimonial by ID
  getById: async (id: string): Promise<Testimonial | null> => {
    const testimonialRef = doc(db, 'testimonials', id);
    const testimonialDoc = await getDoc(testimonialRef);
    
    if (testimonialDoc.exists()) {
      return { id: testimonialDoc.id, ...testimonialDoc.data() } as Testimonial;
    }
    
    return null;
  },

  // Create new testimonial
  create: async (testimonial: Omit<Testimonial, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> => {
    const testimonialsRef = collection(db, 'testimonials');
    const now = new Date().toISOString();
    
    const newTestimonial = {
      ...testimonial,
      createdAt: now,
      updatedAt: now
    };
    
    const docRef = await addDoc(testimonialsRef, newTestimonial);
    return docRef.id;
  },

  // Update testimonial
  update: async (id: string, testimonial: Partial<Testimonial>): Promise<void> => {
    const testimonialRef = doc(db, 'testimonials', id);
    
    await updateDoc(testimonialRef, {
      ...testimonial,
      updatedAt: new Date().toISOString()
    });
  },

  // Delete testimonial
  delete: async (id: string): Promise<void> => {
    const testimonialRef = doc(db, 'testimonials', id);
    await deleteDoc(testimonialRef);
  },

  // Upload testimonial image
  uploadImage: async (file: File, testimonialId: string): Promise<string> => {
    const storageRef = ref(storage, `testimonials/${testimonialId}/${file.name}`);
    await uploadBytes(storageRef, file);
    return getDownloadURL(storageRef);
  }
};

// Products API
export const productsApi = {
  getAll: async (): Promise<Product[]> => {
    const productsRef = collection(db, 'products');
    const snapshot = await getDocs(productsRef);
    return snapshot.docs.map(d => ({ id: d.id, ...d.data() } as Product));
  },

  getActive: async (): Promise<Product[]> => {
    const productsRef = collection(db, 'products');
    const q = query(productsRef, where('status', '==', 'active'));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(d => ({ id: d.id, ...d.data() } as Product));
  },

  create: async (product: Omit<Product, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> => {
    const now = new Date().toISOString();
    const docRef = await addDoc(collection(db, 'products'), { ...product, createdAt: now, updatedAt: now });
    return docRef.id;
  },

  update: async (id: string, product: Partial<Product>): Promise<void> => {
    await updateDoc(doc(db, 'products', id), { ...product, updatedAt: new Date().toISOString() });
  },

  delete: async (id: string): Promise<void> => {
    await deleteDoc(doc(db, 'products', id));
  },

  uploadImage: async (file: File, productId: string): Promise<string> => {
    const storageRef = ref(storage, `products/${productId}/${file.name}`);
    await uploadBytes(storageRef, file);
    return getDownloadURL(storageRef);
  },

  uploadImages: async (files: File[], productId: string): Promise<string[]> => {
    const urls: string[] = [];
    for (const file of files) {
      const storageRef = ref(storage, `products/${productId}/${Date.now()}_${file.name}`);
      await uploadBytes(storageRef, file);
      urls.push(await getDownloadURL(storageRef));
    }
    return urls;
  },

  getById: async (id: string): Promise<Product | null> => {
    const productDoc = await getDoc(doc(db, 'products', id));
    return productDoc.exists() ? ({ id: productDoc.id, ...productDoc.data() } as Product) : null;
  },

  updateQuantity: async (id: string, quantity: number): Promise<void> => {
    await updateDoc(doc(db, 'products', id), { quantity, updatedAt: new Date().toISOString() });
  },
};

// Orders API
export const ordersApi = {
  getAll: async (): Promise<Order[]> => {
    const ordersRef = collection(db, 'orders');
    const q = query(ordersRef, orderBy('createdAt', 'desc'));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(d => ({ id: d.id, ...d.data() } as Order));
  },

  create: async (order: Omit<Order, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> => {
    const now = new Date().toISOString();
    const docRef = await addDoc(collection(db, 'orders'), { ...order, createdAt: now, updatedAt: now });
    return docRef.id;
  },

  update: async (id: string, order: Partial<Order>): Promise<void> => {
    await updateDoc(doc(db, 'orders', id), { ...order, updatedAt: new Date().toISOString() });
  },

  updateStatus: async (id: string, status: Order['status']): Promise<void> => {
    await updateDoc(doc(db, 'orders', id), { status, updatedAt: new Date().toISOString() });
  },

  delete: async (id: string): Promise<void> => {
    await deleteDoc(doc(db, 'orders', id));
  }
};

export const specialRequestsApi = {
  getAll: async (): Promise<SpecialRequest[]> => {
    const snap = await getDocs(query(collection(db, 'specialRequests'), orderBy('createdAt', 'desc')));
    return snap.docs.map(d => ({ id: d.id, ...d.data() } as SpecialRequest));
  },

  // Creates the request and optionally uploads photo in one shot (no update needed = works without auth)
  create: async (req: Omit<SpecialRequest, 'id' | 'createdAt' | 'status'>, photoFile?: File): Promise<string> => {
    // Pre-generate a doc reference so we can use the ID for storage path
    const docRef = doc(collection(db, 'specialRequests'));

    let photoUrl: string | undefined;
    if (photoFile) {
      const storageRef = ref(storage, `specialRequests/${docRef.id}/${photoFile.name}`);
      await uploadBytes(storageRef, photoFile);
      photoUrl = await getDownloadURL(storageRef);
    }

    // Firestore rejects undefined values — strip them out
    const data: Record<string, unknown> = { status: 'new', createdAt: new Date().toISOString() };
    Object.entries(req).forEach(([k, v]) => { if (v !== undefined) data[k] = v; });
    if (photoUrl) data.photoUrl = photoUrl;

    await setDoc(docRef, data);

    return docRef.id;
  },

  update: async (id: string, data: Partial<SpecialRequest>): Promise<void> => {
    await updateDoc(doc(db, 'specialRequests', id), data);
  },

  delete: async (id: string): Promise<void> => {
    await deleteDoc(doc(db, 'specialRequests', id));
  },
};
